import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Search, MapPin, Calendar as CalendarIcon, Users, CreditCard, FileText } from "lucide-react";

const statusColors = {
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  checked_in: "bg-blue-100 text-blue-800 border-blue-200",
  checked_out: "bg-neutral-200 text-neutral-800 border-neutral-300",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};

const MyBooking = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState("id");
  const [bookingId, setBookingId] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params = tab === "id" ? { booking_id: bookingId, last_name: lastName } : { phone };
      const data = await api.lookupBooking(params);
      setResults(data);
    } catch (e) {
      setResults([]);
      const detail = e.response?.data?.detail || e.message;
      toast({ title: "Not found", description: String(detail) });
    } finally {
      setLoading(false);
    }
  };

  const canSearch =
    tab === "id" ? bookingId.trim() && lastName.trim() : phone.trim().length >= 4;

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Header />
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[11px] tracking-[0.25em] font-medium text-[#c8102e] uppercase">Guest Services</span>
            <h1 className="text-3xl md:text-5xl font-serif font-medium mt-2">Find your booking</h1>
            <p className="text-neutral-600 mt-3 max-w-lg mx-auto">Enter your booking details below to view your reservation, check payment status and print your confirmation.</p>
          </div>

          <div className="border border-neutral-200 p-6 md:p-8 bg-white shadow-sm">
            <Tabs value={tab} onValueChange={(v) => { setTab(v); setResults(null); setSearched(false); }}>
              <TabsList className="bg-neutral-100 rounded-none w-full grid grid-cols-2 h-auto p-0">
                <TabsTrigger value="id" className="rounded-none py-3 data-[state=active]:bg-white data-[state=active]:text-[#c8102e] data-[state=active]:shadow-none font-medium">
                  Booking ID + Last Name
                </TabsTrigger>
                <TabsTrigger value="phone" className="rounded-none py-3 data-[state=active]:bg-white data-[state=active]:text-[#c8102e] data-[state=active]:shadow-none font-medium">
                  Phone Number
                </TabsTrigger>
              </TabsList>

              <TabsContent value="id" className="mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Booking ID</Label>
                    <Input value={bookingId} onChange={(e) => setBookingId(e.target.value.toUpperCase())} placeholder="AIP-XYZ123456" className="rounded-none uppercase" />
                  </div>
                  <div>
                    <Label>Last name</Label>
                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Sharma" className="rounded-none" />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="phone" className="mt-6">
                <div>
                  <Label>Phone number</Label>
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98706 27398" className="rounded-none" />
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={search}
                disabled={!canSearch || loading}
                className="bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none px-8 py-5"
              >
                <Search size={16} className="mr-2" /> {loading ? "Searching..." : "Find booking"}
              </Button>
            </div>
          </div>

          {searched && results && results.length === 0 && (
            <div className="text-center py-12 text-neutral-500 border border-dashed border-neutral-300 mt-6">
              No bookings found. Please check your details or contact us at <span className="font-medium text-[#c8102e]">+91 98706 27398</span>.
            </div>
          )}

          {results && results.length > 0 && (
            <div className="mt-8 space-y-5">
              {results.map((b) => (
                <div key={b.booking_id} className="border border-neutral-200 bg-white overflow-hidden">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between bg-neutral-50 px-6 py-4 border-b border-neutral-200">
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-neutral-500">Booking ID</div>
                      <div className="font-mono font-medium text-lg">{b.booking_id}</div>
                    </div>
                    <Badge variant="outline" className={`rounded-none capitalize mt-3 md:mt-0 ${statusColors[b.booking_status]}`}>
                      {b.booking_status.replace("_", " ")}
                    </Badge>
                  </div>

                  <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-serif text-2xl font-medium mb-1">{b.hotel_name}</h3>
                      <p className="text-sm text-neutral-600 flex items-center gap-1.5"><MapPin size={13} className="text-[#c8102e]" /> {b.room_type}</p>

                      <div className="mt-6 space-y-3 text-sm">
                        <Row icon={<CalendarIcon size={14} className="text-[#c8102e]" />} label="Check-in" value={b.check_in} />
                        <Row icon={<CalendarIcon size={14} className="text-[#c8102e]" />} label="Check-out" value={`${b.check_out} · ${b.nights} night${b.nights > 1 ? "s" : ""}`} />
                        <Row icon={<Users size={14} className="text-[#c8102e]" />} label="Guests" value={`${b.guests} guest${b.guests > 1 ? "s" : ""} · ${b.rooms} room${b.rooms > 1 ? "s" : ""}`} />
                      </div>
                    </div>

                    <div>
                      <div className="bg-neutral-50 border border-neutral-200 p-5">
                        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Guest</div>
                        <div className="font-medium text-lg">{b.guest_first_name} {b.guest_last_name}</div>
                        <div className="text-sm text-neutral-600 mt-1">{b.phone}</div>
                        {b.email && <div className="text-sm text-neutral-600">{b.email}</div>}
                      </div>

                      <div className="mt-4 flex items-start gap-3 border border-neutral-200 p-5">
                        <CreditCard size={18} className="text-[#c8102e] mt-0.5" />
                        <div className="flex-1">
                          <div className="text-[11px] uppercase tracking-widest text-neutral-500">Payment</div>
                          <div className="font-medium capitalize mt-0.5">
                            {b.payment_method} · {b.payment_status === "paid" ? "Collected" : "Pending at hotel"}
                          </div>
                          <div className="text-2xl font-serif font-medium mt-2">
                            {b.currency} {Number(b.amount).toLocaleString("en-IN")}
                          </div>
                        </div>
                      </div>

                      {b.notes && (
                        <div className="mt-4 flex items-start gap-3 border border-neutral-200 p-5">
                          <FileText size={16} className="text-[#c8102e] mt-0.5" />
                          <div>
                            <div className="text-[11px] uppercase tracking-widest text-neutral-500">Notes</div>
                            <div className="text-sm text-neutral-800 mt-1">{b.notes}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-neutral-200 px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-neutral-50">
                    <div className="text-xs text-neutral-500">
                      Booked on {new Date(b.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </div>
                    <Button variant="outline" onClick={() => window.open(`/receipt/${b.booking_id}`, "_blank")} className="rounded-none border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white">
                      Print cash receipt
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

const Row = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-5">{icon}</div>
    <div className="text-neutral-500 uppercase text-[10px] tracking-widest w-20">{label}</div>
    <div className="text-neutral-900 font-medium">{value}</div>
  </div>
);

export default MyBooking;
